import React from 'react';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Icon from '../components/AppIcon';

const Product = () => {
  // Enhanced SEO Data
  const seoData = {
    title: 'OctalChip Products - Email Validator SaaS | Professional Email Validation Service',
    description: 'OctalChip offers professional email validation SaaS with 99.9% accuracy. Validate single emails or bulk lists to reduce bounce rates and improve deliverability. Get 100 free credits with company domain signup.',
    keywords: 'OctalChip, email validator, email validation service, bulk email validation, email verification, SaaS email tool, professional email validation, bounce rate reduction, email deliverability, tech company',
    canonical: 'https://www.octalchip.com/product',
    tags: ['OctalChip', 'email validator', 'SaaS', 'email validation', 'email verification', 'bulk validation', 'tech company'],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Products', url: 'https://www.octalchip.com/product' }
    ],
    faqData: [
      {
        question: 'What is OctalChip and what products do you offer?',
        answer: 'OctalChip is a technology company that specializes in SaaS solutions. Our flagship product is the Email Validator SaaS, a professional email validation service with 99.9% accuracy.'
      },
      {
        question: 'What is the accuracy rate of OctalChip\'s email validation service?',
        answer: 'Our email validation service has a 99.9% accuracy rate, ensuring reliable results for your email marketing campaigns.'
      },
      {
        question: 'Do you offer bulk email validation?',
        answer: 'Yes, we support both single email validation and bulk processing for large email lists to help improve your deliverability rates.'
      },
      {
        question: 'How many free credits do I get with OctalChip?',
        answer: 'We offer 100 free credits with company domain signup, allowing you to test our service before committing to a paid plan.'
      }
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "OctalChip",
      "description": "OctalChip is a technology company offering professional SaaS solutions including Email Validator SaaS with 99.9% accuracy",
      "url": "https://www.octalchip.com",
      "logo": "https://www.octalchip.com/octalchiplogo.svg",
      "sameAs": [
        "https://www.octalchip.com"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "OctalChip Products",
        "itemListElement": [
          {
            "@type": "SoftwareApplication",
            "name": "Email Validator SaaS",
            "description": "Professional email validation service with 99.9% accuracy for single and bulk email validation",
            "url": "https://www.octalchip.com/product",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "100 free credits with company domain signup"
            }
          }
        ]
      }
    }
  };

  const heroStatistics = [
    { value: '99.9%', label: 'Accuracy Rate' },
    { value: '100', label: 'Free Credits' },
    { value: 'Bulk', label: 'Processing' },
    { value: 'API', label: 'Integration' }
  ];

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-800 mb-6">
              Email Validator SaaS
            </h1>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists 
              to reduce bounce rates and improve deliverability. Get 100 free credits with company domain signup.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {heroStatistics.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-[#00d690] mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Email Validator SaaS Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Professional Email Validation</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Validate emails with 99.9% accuracy to improve your deliverability and reduce bounce rates
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-[#00d690]/5 to-blue-500/5 rounded-2xl p-8 shadow-lg">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center">
                      <Icon name="Mail" size={24} className="text-[#00d690]" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">Email Validator SaaS</h3>
                      <p className="text-[#00d690] font-semibold">Professional Email Validation Service</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists 
                    to reduce bounce rates and improve deliverability. Get 100 free credits with company domain signup.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">99.9%</div>
                      <div className="text-sm text-gray-600">Accuracy Rate</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">Bulk</div>
                      <div className="text-sm text-gray-600">Processing</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">API</div>
                      <div className="text-sm text-gray-600">Integration</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-2xl font-bold text-[#00d690] mb-1">GDPR</div>
                      <div className="text-sm text-gray-600">Compliant</div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Key Features:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Real-time validation
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Bulk processing
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        Detailed analytics
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-[#00d690] mr-2" />
                        API access
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://mailvalidator.octalchip.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center"
                    >
                      Try Email Validator
                    </a>
                    <a
                      href="/contact"
                      className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center"
                    >
                      Get Custom Quote
                    </a>
                  </div>
            </div>
            
                <div className="text-center">
                  <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-dashed border-[#00d690]/30">
                    <div className="w-20 h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon name="Mail" size={40} className="text-[#00d690]" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-800 mb-4">Free Trial Available</h4>
                    <p className="text-gray-600 mb-6">
                      Get 100 free credits to test our email validation service
                    </p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Free Credits:</span>
                        <span className="font-semibold text-[#00d690]">100</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Validation Type:</span>
                        <span className="font-semibold text-gray-800">Single & Bulk</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Support:</span>
                        <span className="font-semibold text-gray-800">Email Support</span>
                      </div>
                    </div>
                    <a
                      href="https://mailvalidator.octalchip.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block w-full bg-[#00d690] hover:bg-[#00b876] text-white px-6 py-3 rounded-lg font-semibold transition-colors mt-6"
                    >
                      Start Free Trial
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">
              Ready to Improve Your Email Deliverability?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Start validating your emails today with our professional service. Get 100 free credits 
              and experience the difference in your email marketing campaigns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://mailvalidator.octalchip.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#00d690] hover:bg-[#00b876] text-white px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                Start Free Trial
              </a>
              <a
                href="/contact"
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Product; 